import { useRouter } from "next/router";
import { pageRelatedLink } from "@/lib/functions";
import { routerHandler } from "@/lib/routes/router-links-handler";
import { LimitedEditionCard } from "./LimitedEditionCard";

type EditionHeroImage = {
	url?: string;
	responsiveImage?: unknown;
};

type EditionPage = {
	slug?: string;
	hero?: {
		__typename?: string;
		title?: string;
		image?: EditionHeroImage;
		featuredImage?: EditionHeroImage;
		backgroundImage?: EditionHeroImage;
	};
};

type LimitedEditionSelectorProps = {
	sectionBgColor?: { color?: { hex?: string } };
	cardBgColor?: { color?: { hex?: string } };
	textColor?: { color?: { hex?: string; cssRgb?: string } };
	buttonConfig?: any;
	editionSelector?: EditionPage[];
};

function buildEditionHref(slug: string | undefined, locale: string | undefined) {
	if (!slug) return "";

	return routerHandler({
		typename: "BrandsPageRecord",
		slug: pageRelatedLink(
			{ __typename: "BrandsPageRecord", slug },
			locale ?? ""
		),
		locale,
	});
}

function mapEditionCards(
	editionSelector: EditionPage[] | undefined,
	locale: string | undefined
) {
	return (editionSelector ?? [])
		.map((page) => {
			const hero = page?.hero;
			if (!hero?.__typename) return null;

			const image =
				hero.__typename === "SectionHeroFeaturedRecord"
					? hero.featuredImage ?? hero.backgroundImage
					: hero.image;

			if (!image?.responsiveImage && !image?.url) return null;

			const href = buildEditionHref(page.slug, locale);
			if (!href) return null;

			return {
				title: hero.title,
				image,
				href,
			};
		})
		.filter(Boolean) as Array<{
		title?: string;
		image: NonNullable<EditionPage["hero"]>["image"];
		href: string;
	}>;
}

export default function LimitedEditionSelector({
	sectionBgColor,
	cardBgColor,
	textColor,
	buttonConfig,
	editionSelector,
}: LimitedEditionSelectorProps) {
	const { locale } = useRouter();
	const cards = mapEditionCards(editionSelector, locale);

	if (!cards.length) return null;

	return (
		<section
			className="w-full py-16 lg:py-24"
			style={{ backgroundColor: sectionBgColor?.color?.hex }}
		>
			<div className="container mx-auto max-w-[72rem]">
				<div className="grid grid-cols-1 gap-40 overflow-visible pt-10 pb-10 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3 lg:gap-8 lg:pt-28">
					{cards.map((card, index) => (
						<LimitedEditionCard
							key={`${card.href}-${index}`}
							title={card.title}
							image={card.image}
							href={card.href}
							cardBgColor={cardBgColor?.color?.hex}
							textColor={textColor?.color}
							buttonConfig={buttonConfig}
						/>
					))}
				</div>
			</div>
		</section>
	);
}

export const fragment = `
  fragment LimitedEditionSelectorFragment on LimitedEdtSelectorRecord {
    id
    name
    cardBgColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    sectionBgColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    textColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    buttonConfig {
      id
      buttonStyle
      addIcon
      labelColor {
        color {
          hex
          cssRgb
        }
      }
      typeColor {
        color {
          hex
          cssRgb
        }
      }
      iconColor {
        color {
          hex
        }
      }
      icon {
        img {
          url
          width
          height
        }
      }
    }
    editionSelector {
      slug
      hero {
        __typename
        ... on SectionHeroImageRecord {
          title
          image {
            url
            alt
            format
            focalPoint {
              x
              y
            }
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
        }
        ... on SectionHeroFeaturedRecord {
          title
          featuredImage {
            url
            alt
            format
            focalPoint {
              x
              y
            }
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
          backgroundImage {
            url
            alt
            format
            focalPoint {
              x
              y
            }
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
        }
      }
    }
  }
`;
